import mailSpaceTooltip from 'img/safeTip.png';
import React, { type RefObject, type VFC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_AUTO_TEST_HIDE } from 'reactApp/appHelpers/configHelpers';
import { overquotaSafeTip, overquotaSafeTipEveryDay } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { sendDwhTooltip } from 'reactApp/modules/dwh/dwh.helpers';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipAppearance, ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { renderSafePromoDialog } from 'reactApp/ui/Toolbar/Move2SafeButton/SafePromoDialog';

import styles from './Move2SafeTooltip.css';

interface IProps {
    targetRef: RefObject<HTMLDivElement>;
}

const TOOLTIP_ID = 'move-2-safe-tooltip-';
const getTipId = (storage: EStorageType) => TOOLTIP_ID + storage;
const name = 'move-2-safe-tip';

export const Move2SafeTooltip: VFC<IProps> = ({ targetRef }) => {
    const dispatch = useDispatch();
    const [show, setShow] = useState(false);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const storage = useSelector(getCurrentStorage);
    const { isTrashBin } = getStorage(storage);

    const tipId = getTipId(storage);
    const wasShown = storeHelper.getValue(tipId);
    const periodPassed = overquotaSafeTipEveryDay && wasShown && storeHelper.isPassed(tipId, { daysCount: 1 });

    const allowShowTip = !IS_AUTO_TEST_HIDE && !isCorpUser && overquotaSafeTip && (!wasShown || periodPassed);

    if (periodPassed) {
        storeHelper.clearStore(tipId);
    }

    const handleClose = useCallback(() => {
        setShow(false);
        dispatch(UserStorageActions.set(tipId, true));

        sendDwhTooltip({ action: 'close', name, place: storage });
    }, [dispatch]);

    const handleClick = useCallback(() => {
        setShow(false);

        sendDwhTooltip({ action: 'click', name, place: storage });

        if (isTrashBin) {
            renderSafePromoDialog();
        }
    }, [isTrashBin]);

    const handleShow = useCallback(() => {
        storeHelper.markAsShown(tipId);

        sendDwhTooltip({ name, place: storage });
    }, []);

    useEffect(() => {
        if (targetRef?.current && allowShowTip) {
            setShow(true);
        }
    }, [targetRef?.current, allowShowTip]);

    if (!show) {
        return null;
    }

    return (
        <FloatingTooltip
            target={targetRef}
            className={styles.root}
            contentClassName={styles.tipContent}
            placement={ETooltipPlacement.bottom}
            appearance={ETooltipAppearance.blue}
            size={ETooltipSize.large}
            title={'Сейф вместо удаления'}
            text={
                <div className={styles.tipText}>
                    Файлы сохранятся, но место в Облаке
                    <br />
                    не займут. Если нужны — можно вернуть
                </div>
            }
            qaId={tipId}
            img={mailSpaceTooltip}
            buttonText={isTrashBin ? 'Подробнее' : 'Попробую'}
            buttonFitContent
            closeOnOutsideClick={true}
            onCrossClose={handleClose}
            onClose={handleClose}
            onClick={handleClick}
            onShow={handleShow}
            imageWidth={112}
        />
    );
};

Move2SafeTooltip.displayName = 'Move2SafeTooltip';
